/******************************************************************************

BabiGO
Profile Dropdown

Menu du compte utilisateur connecté.

Fonctionnalités :

* Informations utilisateur
* Mon profil
* Sécurité
* Paramètres
* Déconnexion
* Fermeture extérieure
* Fermeture avec Escape
  ==============================================================================
  */

import {

    useCallback,

    useEffect,

    useRef,

    useState,

} from "react";

import {

    FiChevronRight,

    FiLogOut,

    FiLock,

    FiSettings,

    FiUser,
} from "react-icons/fi";

import { useNavigate } from "react-router-dom";

import boyAvatar from '../../assets/images/avatars/boy.jpg';
import girlAvatar from '../../assets/images/avatars/girl.jpg';

import useAuth from "../../hooks/useAuth";

import "./profileDropdown.css";

/* ============================================================================
FORMATAGE DU RÔLE
============================================================================ */

function formatRole(role) {

    const roles = {

        passenger: "Passager",

        driver: "Conducteur",

        fleet_manager: "Gestionnaire de flotte",

        support: "Support",

        admin: "Administrateur",

        investor: "Investisseur",

    };


    return roles[role] || "Utilisateur";

}

/* ============================================================================
COMPOSANT
============================================================================ */

export default function ProfileDropdown({

    isOpen,

    onClose,

}) {

    const navigate = useNavigate();


    const dropdownRef = useRef(null);


    const {

        user,

        logout,

    } = useAuth();


    const [

        loggingOut,

        setLoggingOut,

    ] = useState(false);


    /* ========================================================================
    FERMETURE AVEC ESCAPE
    ======================================================================== */

    useEffect(() => {

        if (!isOpen) {

            return undefined;

        }


        const handleKeyDown = (event) => {

            if (event.key === "Escape") {

                onClose();

            }

        };


        document.addEventListener(

            "keydown",

            handleKeyDown

        );


        return () => {

            document.removeEventListener(

                "keydown",

                handleKeyDown

            );

        };

    }, [

        isOpen,

        onClose,

    ]);


    /* ========================================================================
    CLIC EXTÉRIEUR
    ======================================================================== */

    useEffect(() => {

        if (!isOpen) {

            return undefined;

        }


        const handlePointerDown = (event) => {

            if (

                dropdownRef.current &&

                !dropdownRef.current.contains(

                    event.target

                )

            ) {

                onClose();

            }

        };


        document.addEventListener(

            "pointerdown",

            handlePointerDown

        );


        return () => {

            document.removeEventListener(

                "pointerdown",

                handlePointerDown

            );

        };

    }, [

        isOpen,

        onClose,

    ]);


    /* ========================================================================
    NAVIGATION
    ======================================================================== */

    const handleNavigate = useCallback(

        (path) => {

            onClose();

            navigate(path);

        },

        [

            navigate,

            onClose,

        ]

    );


    /* ========================================================================
    DÉCONNEXION
    ======================================================================== */

    const handleLogout = useCallback(

        async () => {

            if (loggingOut) {

                return;

            }


            try {

                setLoggingOut(true);


                await logout();


                /*
                ----------------------------------------------------------------
                Firebase est maintenant déconnecté.

                useAuth() recevra automatiquement :

                    user = null

                via onAuthStateChanged().
                ----------------------------------------------------------------
                */


                onClose();


                navigate(

                    "/login",

                    {

                        replace: true,

                    }

                );


            } catch (error) {

                console.error(

                    "[ProfileDropdown] Erreur de déconnexion :",

                    error

                );


                setLoggingOut(false);

            }

        },

        [

            loggingOut,

            logout,

            navigate,

            onClose,

        ]
    );



    /* ========================================================================
    UTILISATEUR
    ======================================================================== */

    if (!isOpen) {

        return null;

    }


    if (!user) {

        return null;

    }

    const displayName =

        user.fullName ||

        user.displayName ||

        `${user.nom ?? ""} ${user.prenom ?? ""}`.trim() ||

        "Utilisateur";

    const email =

        user.email ||

        "Email non disponible";


    const role =

        user.role ||

        "passenger";


    return (

        <div

            ref={dropdownRef}

            className="profile-dropdown"

            role="menu"

            aria-label="Menu du compte utilisateur"

        >


            {/* ==============================================================
                IDENTITÉ
            ============================================================== */}

            <div className="profile-dropdown-header">

                <div className="profile-dropdown-avatar">

                    {

                        user.avatar ? (

                            <img
                                src={user.avatar === "boy" ? boyAvatar : girlAvatar }
                                alt={displayName.charAt(0).toUpperCase()}
                                className="workspace-icon"
                            />

                        ) : (

                            <FiUser />

                        )

                    }

                </div>


                <div className="profile-dropdown-identity">

                    <div className="profile-dropdown-name">

                        {displayName}

                    </div>


                    <div className="profile-dropdown-role">

                        {formatRole(role)}

                    </div>


                    <div className="profile-dropdown-email">

                        {email}

                    </div>

                </div>

            </div>


            {/* ==============================================================
                SÉPARATION
            ============================================================== */}

            <div className="profile-dropdown-divider" />


            {/* ==============================================================
                MON PROFIL
            ============================================================== */}

            <button

                type="button"

                className="profile-dropdown-item"

                role="menuitem"

                onClick={() =>

                    handleNavigate("/account")

                }

            >

                <FiUser />

                <span>

                    Mon profil

                </span>


                <FiChevronRight

                    className="profile-dropdown-arrow"

                />

            </button>


            {/* ==============================================================
                SÉCURITÉ
            ============================================================== */}

            <button

                type="button"

                className="profile-dropdown-item"

                role="menuitem"

                onClick={() =>

                    handleNavigate(

                        "/account/security"

                    )

                }

            >

                <FiLock />

                <span>

                    Sécurité et mot de passe

                </span>


                <FiChevronRight

                    className="profile-dropdown-arrow"

                />

            </button>


            {/* ==============================================================
                PARAMÈTRES
            ============================================================== */}

            <button

                type="button"

                className="profile-dropdown-item"

                role="menuitem"

                onClick={() =>

                    handleNavigate("/settings")

                }

            >

                <FiSettings />

                <span>

                    Paramètres

                </span>


                <FiChevronRight

                    className="profile-dropdown-arrow"

                />

            </button>


            {/* ==============================================================
                SÉPARATION
            ============================================================== */}

            <div className="profile-dropdown-divider" />


            {/* ==============================================================
                DÉCONNEXION
            ============================================================== */}

            <button

                type="button"

                className="profile-dropdown-item logout-item"

                role="menuitem"

                onClick={handleLogout}

                disabled={loggingOut}

            >

                <FiLogOut

                    className={

                        loggingOut

                            ? "logout-icon spinning"

                            : "logout-icon"

                    }

                />


                <span>

                    {

                        loggingOut

                            ? "Déconnexion..."

                            : "Se déconnecter"

                    }

                </span>

            </button>

        </div>

    );

}
